import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Spinner, Alert } from "react-bootstrap";

import { useTitle } from "./context";
import { SpellBookBackend } from "./backend";
import { storeState, loadState } from "./utilities";
import { AlphabeticalSpellsList } from "./spellsList";

import "bootstrap/dist/css/bootstrap.min.css";


const SCHOOL_PAGE_KEY = "school-page";


async function fetchSpells(schoolId) {
    return await SpellBookBackend.listSpellsBySchool(schoolId);
}


export function SchoolPage({navbarHeight}) {
    const { schoolId } = useParams();

    const navigate = useNavigate();
    const { setTitle } = useTitle();
    const [schoolInfo, setSchoolInfo] = useState(null);
    const [spells, setSpells] = useState([]);
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [error, setError] = useState("");

    const getStateJson = () => {
        return {
            schoolId: schoolId,
            schoolInfo: schoolInfo,
            spells: spells
        };
    };

    const setStateJson = (s) => {
        if (s.schoolId !== schoolId) throw new Error("Attempting to set a state with mismatching school ID");
        setSchoolInfo(s.schoolInfo);
        setSpells(s.spells);
    };
    
    useEffect(() => {
        setTitle(schoolInfo?.name);
    }, [setTitle, schoolInfo]);

    useEffect(() => {
        if (schoolInfo && spells && spells.length > 0) {
            storeState(SCHOOL_PAGE_KEY, getStateJson());
        }
    }, [schoolInfo, spells]);

    const fetchData = async () => {
        setSchoolInfo(await SpellBookBackend.getSchool(schoolId));
        setSpells(await fetchSpells(schoolId));
    }

    useEffect(() => {
        let mounted = true;
        (
            async () => {
                try {
                    setStatus("loading");
                    const s = loadState(SCHOOL_PAGE_KEY);
                    if (s?.schoolId === schoolId && s?.spells?.length > 0) {
                        setStateJson(s);
                    }
                    else {
                        await fetchData();
                    }
                    if (mounted) {
                        setStatus("success");
                    }
                }
                catch {
                    if (mounted) {
                        setError("Failed to load items.");
                        setStatus("error");
                    }
                }
            }
        )();
        return () => {
            mounted = false;
        };
    }, []);

    const content = useMemo(() => {
        if (status === "loading") {
            return (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status" aria-label="Loading items" />
                </div>
            );
        }
        if (status === "error") {
            return <Alert variant="danger" className="my-3">{error}</Alert>;
        }
        if (spells.length === 0) {
            return <Alert variant="secondary" className="my-3">No spells found.</Alert>;
        }

        //else

        const spellsListHeight = `calc(100vh - ${navbarHeight}px)`;

        return (
            <AlphabeticalSpellsList
            stateKey={`${SCHOOL_PAGE_KEY}.${schoolId}`}
            spells={spells}
            onSpellClick={(spellId) => navigate(`/spells/${spellId}`)}
            height={spellsListHeight}/>
        );
    }, [status, spells, error, navigate]);

    return (
        <Container
        fluid
        className="bg-light p-0"
        style={{ marginTop: `${navbarHeight}px` }}>
            {content}
        </Container>
    );
}
